import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { alertManagementService } from '../../services/alertManagementService';
import { useAuth } from '../../contexts/AuthContext';
import IncidentListTable from './components/IncidentListTable';
import SeverityFilter from './components/SeverityFilter';
import IncidentDetailModal from './components/IncidentDetailModal';
import BulkActionsBar from './components/BulkActionsBar';
import IncidentStats from './components/IncidentStats';
import EscalationConfigModal from './components/EscalationConfigModal';

const AlertManagement = () => {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [selectedIncidents, setSelectedIncidents] = useState([]);
  const [showEscalationConfig, setShowEscalationConfig] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedSeverity, selectedStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (selectedSeverity !== 'all') filters.severity = selectedSeverity;
      if (selectedStatus !== 'all') filters.status = selectedStatus;

      const [incidentsData, statsData] = await Promise.all([
        alertManagementService?.getIncidents(filters),
        alertManagementService?.getIncidentStats()
      ]);

      setIncidents(incidentsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading alert data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleAcknowledge = async (incidentId) => {
    try {
      await alertManagementService?.acknowledgeIncident(incidentId, user?.id);
      await loadData();
      setSelectedIncidents([]);
    } catch (error) {
      console.error('Error acknowledging incident:', error);
    }
  };

  const handleBulkAcknowledge = async () => {
    try {
      await alertManagementService?.bulkAcknowledgeIncidents(selectedIncidents, user?.id);
      await loadData();
      setSelectedIncidents([]);
    } catch (error) {
      console.error('Error bulk acknowledging incidents:', error);
    }
  };

  const handleResolve = async (incidentId, notes) => {
    try {
      await alertManagementService?.resolveIncident(incidentId, user?.id, notes);
      await loadData();
      setSelectedIncident(null);
    } catch (error) {
      console.error('Error resolving incident:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center p-1.5">
              <img 
                src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                alt="DraftIQ Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Alert Management</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowEscalationConfig(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Escalation Rules
            </button>
          </div>
        </div>

        {/* Stats Dashboard */}
        {stats && <IncidentStats stats={stats} />}

        {/* Filters */}
        <SeverityFilter
          selectedSeverity={selectedSeverity}
          selectedStatus={selectedStatus}
          onSeverityChange={setSelectedSeverity}
          onStatusChange={setSelectedStatus}
        />

        {/* Bulk Actions */}
        {selectedIncidents?.length > 0 && (
          <BulkActionsBar
            selectedCount={selectedIncidents?.length}
            onAcknowledge={handleBulkAcknowledge}
            onClear={() => setSelectedIncidents([])}
          />
        )}

        {/* Incidents Table */}
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : (
            <IncidentListTable
              incidents={incidents}
              selectedIncidents={selectedIncidents}
              onSelectIncident={(id) => {
                setSelectedIncidents(prev =>
                  prev?.includes(id)
                    ? prev?.filter(i => i !== id)
                    : [...prev, id]
                );
              }}
              onSelectAll={(checked) => {
                setSelectedIncidents(checked ? incidents?.map(i => i?.id) : []);
              }}
              onViewDetails={setSelectedIncident}
              onAcknowledge={handleAcknowledge}
            />
          )}
        </div>
      </div>
      {/* Incident Detail Modal */}
      {selectedIncident && (
        <IncidentDetailModal
          incident={selectedIncident}
          onClose={() => setSelectedIncident(null)}
          onResolve={handleResolve}
          onAcknowledge={handleAcknowledge}
        />
      )}
      {/* Escalation Config Modal */}
      {showEscalationConfig && (
        <EscalationConfigModal
          userId={user?.id}
          onClose={() => setShowEscalationConfig(false)}
        />
      )}
    </div>
  );
};

export default AlertManagement;